'use client'
import { localizedStrings } from '@core/strings'
import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useModal } from '@common/GlobalModal'
import {
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  DialogActions,
  TextInput,
} from '@common/material'
import { Clear } from '@mui/icons-material'
import { ModalDialogContainer } from '@common/modal'
import { useFileSystem } from '@core/react-query/features/folders/hooks'
import BackdropLoader from '@common/fundamentals/BackdropLoader'
import { Box, Flex, Text } from '@fundamentals'
import { PspdfkitComponentRef } from 'components/modal/pdf/PspdfkitComponent'
import { FileFormat, SiteBoxDocument } from '@core/entities/SiteBoxDocument'
import { FileTreeItem } from '../components/FileTreeItem'
import { FolderTreeNode } from '../components/FolderTreeNode'
import { SiteBoxDocumentPreview } from '../components/SiteBoxDocumentPreview'
import { useDebouncedState } from '@core/utils/useDebouncedState'
import { SiteBoxDocumentSidebar } from '@modules/siteBoxDocumentSidebar'

export { siteBoxDocumentToProof } from '../components/types'

interface ComponentType {
  organisationId: number
  projectId: number
  multiSelect?: boolean
  value?: Array<SiteBoxDocument>
  onChange?: (siteboxDocuments: SiteBoxDocument | SiteBoxDocument[]) => void
  onClose?: () => void
  readOnly?: boolean
  previewUrl?: string // when set, takes precedence over currentVersion.documentGetUrl
  // Restrict which document formats can be linked. Defaults to PDF only.
  allowedFormats?: FileFormat[]
}

export const SelectSiteBoxDocumentModal: React.FC<ComponentType> = ({
  organisationId,
  projectId,
  multiSelect = false,
  value = [],
  onChange,
  onClose,
  readOnly = false,
  previewUrl,
  allowedFormats = ['PDF'],
}) => {
  const { hideModal } = useModal()
  const pspdfkitRef = useRef<PspdfkitComponentRef>(null)
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set())

  const [selectedSiteBoxDocuments, setSelectedSiteBoxDocuments] = useState<
    Array<SiteBoxDocument>
  >(value.map(SiteBoxDocument.new))

  const [previewDocument, setPreviewDocument] = useState<SiteBoxDocument>(
    () => {
      return selectedSiteBoxDocuments.length
        ? selectedSiteBoxDocuments[0]
        : undefined
    },
  )

  const [searchTerm, setSearchTerm] = useDebouncedState('', 300)
  const isFiltering = !!searchTerm

  const matchesFilter = useCallback(
    (file: SiteBoxDocument) => {
      if (!file.name.toLowerCase().includes(searchTerm.toLowerCase()))
        return false
      return true
    },
    [searchTerm],
  )

  const isFileAllowed = useCallback(
    (file: SiteBoxDocument) => {
      const format = file.currentVersion?.format
      if (!format) return false
      return allowedFormats.includes(format)
    },
    [allowedFormats],
  )

  // Combined predicate threaded to FileTreeItem / FolderTreeNode: a file shows
  // only if its format is allowed for this question and it matches the search.
  const filterFile = useCallback(
    (file: SiteBoxDocument) => {
      if (!isFileAllowed(file)) return false
      if (isFiltering && !matchesFilter(file)) return false
      return true
    },
    [isFileAllowed, isFiltering, matchesFilter],
  )

  const { folder, isLoading } = useFileSystem({
    organisationId,
    projectId,
    type: 'SITE_BOX',
    folderId: -1,
    status: ['LIVE'],
  })

  const handleToggleFolder = useCallback((folderId: number) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }, [])

  const handleSelect = useCallback(
    (item: SiteBoxDocument) => {
      const currentVersion = item.currentVersion
      if (!currentVersion) return

      setPreviewDocument(item)
      if (multiSelect) {
        setSelectedSiteBoxDocuments((prev) => {
          const isAlreadySelected = prev.some((doc) => doc.id === item.id)
          if (isAlreadySelected) {
            return prev.filter((doc) => doc.id !== item.id)
          }
          return [...prev, item]
        })
      } else {
        onChange(item)
        hideModal()
      }
    },
    [multiSelect, onChange, hideModal],
  )

  useEffect(() => {
    if (previewDocument || !folder?.files.length) return
    const firstAllowed = (folder.files as SiteBoxDocument[]).find(isFileAllowed)
    if (firstAllowed) {
      setPreviewDocument(firstAllowed)
    }
  }, [folder?.files, isFileAllowed])

  if (!folder && isLoading) return <BackdropLoader open />

  const files = ((folder?.files ?? []) as SiteBoxDocument[]).filter(filterFile)

  const handleConfirmSelection = () => {
    onChange(selectedSiteBoxDocuments)
    hideModal()
  }

  const previewVersion = previewDocument?.currentVersion
  const previewMode = previewVersion?.getPreviewMode() ?? 'PDF'

  return (
    <ModalDialogContainer
      PaperProps={{
        sx: { maxWidth: '95vw', height: '95vh', flex: 1 },
      }}
      maxWidth='md'
      onClose={() => {
        hideModal()
        onClose && onClose()
      }}
    >
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20, mb: 0 }}
      >
        <Box>{localizedStrings.addDocument}</Box>

        <IconButton
          aria-label='close-modal'
          onClick={() => {
            hideModal()
            onClose && onClose()
          }}
        >
          <Clear />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'row',
          px: 1,
          pb: 1,
          pt: 0,
          borderBottom: '0.5px solid',
          borderTop: '0.5px solid',
          borderColor: 'divider',
        }}
      >
        {!readOnly && (
          <Flex
            sx={{
              flex: 1,
              overflowY: 'auto',
              flexDirection: 'column',
              borderRight: '0.5px solid',
              borderColor: 'divider',
            }}
          >
            <TextInput
              placeholder={localizedStrings.search}
              onChange={(e) => setSearchTerm(e.target.value)}
              size='small'
              fullWidth
              sx={{ pl: 1, pr: 2, py: 1 }}
            />
            <Flex
              justifyContent='space-between'
              alignItems='center'
              px={2}
              py={1}
            >
              <Text fontSize={12} color='text.secondary'>
                {localizedStrings.selected(selectedSiteBoxDocuments.length)}
              </Text>
              <Button
                variant='text'
                size='small'
                onClick={() => setSelectedSiteBoxDocuments([])}
                sx={{ fontSize: 12, textTransform: 'none', minWidth: 0 }}
              >
                {localizedStrings.clearAll}
              </Button>
            </Flex>

            {isFiltering ? (
              <>
                {files.map((file) => (
                  <FileTreeItem
                    key={file.id}
                    file={file}
                    showPath
                    multiSelect={multiSelect}
                    selectedSiteBoxDocuments={selectedSiteBoxDocuments}
                    onSelectFile={handleSelect}
                  />
                ))}
                {folder.folders.map((subfolder) => (
                  <FolderTreeNode
                    key={subfolder.id}
                    folder={subfolder}
                    organisationId={organisationId}
                    projectId={projectId}
                    flatMode
                    multiSelect={multiSelect}
                    selectedSiteBoxDocuments={selectedSiteBoxDocuments}
                    onSelectFile={handleSelect}
                    matchesFilter={filterFile}
                  />
                ))}
              </>
            ) : (
              <>
                {folder.folders.map((subfolder) => (
                  <FolderTreeNode
                    key={subfolder.id}
                    folder={subfolder}
                    organisationId={organisationId}
                    projectId={projectId}
                    multiSelect={multiSelect}
                    selectedSiteBoxDocuments={selectedSiteBoxDocuments}
                    expandedFolders={expandedFolders}
                    onToggleFolder={handleToggleFolder}
                    onSelectFile={handleSelect}
                    matchesFilter={filterFile}
                  />
                ))}
                {files.map((file) => (
                  <FileTreeItem
                    key={file.id}
                    file={file}
                    multiSelect={multiSelect}
                    selectedSiteBoxDocuments={selectedSiteBoxDocuments}
                    onSelectFile={handleSelect}
                  />
                ))}
              </>
            )}
          </Flex>
        )}
        <Box sx={{ flex: 3, display: 'flex', flexDirection: 'row' }}>
          <Box sx={{ flex: 2, overflow: 'hidden' }}>
            {previewDocument && (
              <SiteBoxDocumentPreview
                siteBoxDocument={previewDocument}
                documentUrl={previewUrl}
                name={previewDocument.name}
                pspdfkitRef={pspdfkitRef}
                readOnly
              />
            )}
          </Box>
          {previewDocument && previewMode === 'PDF' && (
            <Box sx={{ flex: 1, position: 'relative' }}>
              <SiteBoxDocumentSidebar
                key={previewDocument.id}
                organisationId={organisationId}
                projectId={projectId}
                siteBoxDocument={previewDocument}
                pspdfkitRef={pspdfkitRef}
                readOnly
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ pb: 2, pr: 3 }}>
        {multiSelect && (
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirmSelection}
            sx={{ width: 150, mt: 2, alignSelf: 'flex-end' }}
          >
            {localizedStrings.addDocuments}
          </Button>
        )}
      </DialogActions>
    </ModalDialogContainer>
  )
}
